<template>
  <div class="uk-container">
    <div class="uk-card uk-card-default block-filters">
      <div class="filters">
        <div class="filter">
          <div class="filter-search">
            <div class="search-icon">
              <i class="icon gg-search" />
            </div>
            <div class="search-input">
              <input v-model="filters.search" class="uk-input uk-form-small" type="text" placeholder="Filter Table">
            </div>
          </div>
        </div>

        <div v-if="!isDentist" class="filter">
          <multiselect v-model="filters.dentist" class="filter-dentist multiselect-small" placeholder="Filter by Dentist" label="name" track-by="id" :options="optionsDentists" :multiple="false" :searchable="true" :hide-selected="true" select-label="" />
        </div>

        <div v-if="isDentist" class="filter">
          <multiselect v-model="filters.date" class="filter-dentist multiselect-small" placeholder="Filter by Date" label="value" track-by="id" :options="optionsDates" :multiple="false" :searchable="true" :hide-selected="true" select-label="" />
        </div>
      </div>

      <div class="actions">
        <div class="action">
          <button class="uk-button uk-button-secondary uk-button-small" @click="clearFilters">Clear Filters</button>
        </div>
        <div class="action">
          <router-link :to="{ name: 'appointment-search' }" class="uk-button uk-button-primary uk-button-small">Advanced Search</router-link>
        </div>
      </div>
    </div>

    <div class="uk-card uk-card-default table">
      <vue-good-table
        :columns="columns"
        :rows="rows"
        :sort-options="{ initialSortBy: {field: 'appointmentTimestamp', type: 'desc'} }"
        :pagination-options="$globals.vgt.pagination"
        :row-style-class="rowStyleClassFn"
        style-class="uk-table uk-table-middle uk-table-divider uk-margin-remove datatable dt-appointments"
        :search-options="{ externalQuery: filters.search }"
        @on-row-click="onRowClick"
      >
        <template slot="table-column" slot-scope="props">
          <span v-html="formatLabel(props.column.compositeLabel, props.column.label)" />
        </template>

        <template slot="table-row" slot-scope="props">
          <template v-if="props.column.field === 'appointmentTimestamp'">
            <div :uk-tooltip="`title: ${$moment(props.row.appointmentTimestamp, 'X').fromNow()}`">
              <p>{{ $moment(props.row.appointmentTimestamp, 'X').format('DD/MM/YYYY') }}</p>
              <p>{{ $moment(props.row.appointmentTimestamp, 'X').format('HH:mm:ss') }}</p>
            </div>
          </template>

          <template v-else-if="props.column.field === 'appointmentDate'">
            <div :uk-tooltip="`title: ${$helpers.timeBits.find(b => b.id === props.row.appointmentDate.bit).time.long}`">
              <p>{{ $moment(props.row.appointmentDate.date, 'YYMMDD').format('DD/MM/YYYY') }}</p>
              <p>{{ $helpers.timeBits.find(b => b.id === props.row.appointmentDate.bit).time.short }}</p>
            </div>
          </template>

          <template v-else-if="props.column.field === 'appointmentStatus'">
            <span v-if="props.formattedRow[props.column.field] === 0" class="uk-label uk-label-warning">Incomplete</span>
            <span v-else-if="props.formattedRow[props.column.field] === 1" class="uk-label uk-label-success">Booked</span>
            <span v-else-if="props.formattedRow[props.column.field] === 2" class="uk-label uk-label-danger">Cancelled</span>
            <span v-else class="uk-label uk-label-danger">Error</span>
          </template>

          <template v-else-if="props.column.field === 'paymentStatus'">
            <span v-if="props.formattedRow[props.column.field] === 'succeeded'" class="uk-label uk-label-success">Success</span>
            <span v-else-if="props.formattedRow[props.column.field] === 'requires_payment_method'" class="uk-label uk-label-warning">Incomplete</span>
            <span v-else class="uk-label uk-label-danger">Error</span>
          </template>

          <template v-else>
            {{ props.formattedRow[props.column.field] }}
          </template>
        </template>

        <div slot="emptystate">
          <template v-if="filterStatus">
            <p>No appointments in the last {{ noDataMsg }} match the search criteria!</p>
            <p>You can try <router-link :to="{ name: 'appointment-search' }">Advanced search</router-link>, to include older appointments in the query.</p>
          </template>

          <template v-else>
            There are no appointments in the last {{ noDataMsg }}!
          </template>
        </div>
      </vue-good-table>
    </div>
  </div>
</template>

<script>
import nowMixin from '@/utils/mixins/now'

export default {
  name: 'ViewAppointmentList',

  mixins: [nowMixin],

  data() {
    return {
      filters: {
        search: null,
        dentist: null,
        date: null
      }
    }
  },

  computed: {
    filterStatus() {
      return !Object.values(this.filters).every((filter) => filter === null)
    },

    rows() {
      const source = this.$store.state.appointments.collection
      let response = []

      source.forEach((item) => {
        response.push({
          id: item.id,
          name: `${item.customer.name.first} ${item.customer.name.last}`,
          phone: item.customer.contact.phone,
          email: item.customer.contact.email,
          appointmentTimestamp: item.info.created.timestamp.seconds,
          appointmentDate: !this.isDentist ? item.dentist.data[item.info.dentist] : item.dentist.data[this.$store.state.account.profile.dentist],
          appointmentStatus: !this.isDentist ? item.info.status : item.dentist.data[this.$store.state.account.profile.dentist].status,
          dentist: !this.isDentist ? this.getDentist(item.info.dentist) : '',
          operator: !this.isDentist ? this.getOperator(item.info.created.user) : '',
          paymentStatus: !this.isDentist && item.payment ? item.payment.status : '',
          read: this.isDentist ? item.dentist.data[this.$store.state.account.profile.dentist].read : item.dentist.data[item.info.dentist].read
        })
      })

      if (this.filters.dentist) {
        response = response.filter((item) => item.dentist === this.filters.dentist.name)
      }

      if (this.filters.date) {
        response = response.filter((item) => item.appointmentDate.date === this.filters.date.value)
      }

      return response.reverse()
    },

    columns() {
      const columns = {
        appointmentId: {
          hidden: true,
          field: 'id'
        },
        customerName: {
          compositeLabel: true,
          label: [
            {
              value: 'Customer',
              small: true
            },
            {
              value: 'Name',
              small: false
            }
          ],
          field: 'name'
        },
        customerPhone: {
          compositeLabel: true,
          label: [
            {
              value: 'Customer',
              small: true
            },
            {
              value: 'Phone',
              small: false
            }
          ],
          field: 'phone',
          sortable: false
        },
        customerEmail: {
          compositeLabel: true,
          label: [
            {
              value: 'Customer',
              small: true
            },
            {
              value: 'Email',
              small: false
            }
          ],
          field: 'email',
          sortable: false
        },
        appointmentTimestamp: {
          compositeLabel: true,
          label: [
            {
              value: 'Appointment',
              small: true
            },
            {
              value: 'Created',
              small: false
            }
          ],
          field: 'appointmentTimestamp',
          type: 'number'
        },
        appointmentDate: {
          compositeLabel: true,
          label: [
            {
              value: 'Appointment',
              small: true
            },
            {
              value: 'Date',
              small: false
            }
          ],
          field: 'appointmentDate',
          type: 'number',
          sortFn: this.sortAppointmentDate
        },
        appointmentStatus: {
          compositeLabel: true,
          label: [
            {
              value: 'Appointment',
              small: true
            },
            {
              value: 'Status',
              small: false
            }
          ],
          field: 'appointmentStatus',
          type: 'number'
        },
        dentist: {
          compositeLabel: false,
          label: 'Dentist',
          field: 'dentist'
        },
        operator: {
          compositeLabel: false,
          label: 'Operator',
          field: 'operator'
        },
        paymentStatus: {
          compositeLabel: true,
          label: [
            {
              value: 'Payment',
              small: false
            },
            {
              value: 'Status',
              small: true
            }
          ],
          field: 'paymentStatus'
        }
      }

      if (this.isDentist) {
        delete columns.dentist
        delete columns.operator
        delete columns.paymentStatus
      }

      // Convert to Array
      return Object.values(columns)
    },

    isDentist() {
      return this.$store.getters['account/type'] === 'dentist'
    },

    noDataMsg() {
      let response = 'ERROR'

      if (this.$store.getters['account/type'] === 'dentist') {
        response = '7 days'
      }

      if (this.$store.getters['account/type'] === 'operator') {
        response = '48 hours'
      }

      if (this.$store.getters['account/type'] === 'admin') {
        response = '14 days'
      }

      return response
    },

    optionsDentists() {
      const source = this.$store.state.dentists.collection
      return source.map(({ id, name }) => ({ id, name }))
    },

    optionsDates() {
      // This roundabout approach is required due to Vue's reactivity
      const start = this.$moment(this.now.date.t, 'YYMMDD').subtract(6, 'day')
      const end = this.$moment(this.now.date.t, 'YYMMDD')
      // Get range of last 7 dates with moment and moment-range
      const range = this.$moment.range(start, end)
      // Convert range to Array
      const rangeArray = Array.from(range.by('day'))
      // Format Array data to match data structure
      return rangeArray.map((v) => ({
        value: v.format('DD/MM/YYYY'), // human-readable
        id: v.format('YYMMDD') // technical
      })).reverse()
    }
  },

  created() {
    window.addEventListener('resize', this.calculateFiltersWidth)
  },

  mounted() {
    this.calculateFiltersWidth()
  },

  destroyed() {
    window.removeEventListener('resize', this.calculateFiltersWidth)
  },

  methods: {
    getDentist(i) {
      const dentist = i ? this.$store.getters['dentists/find'](i) : null
      return dentist ? dentist.name : 'N/A'
    },

    getOperator(i) {
      const operator = i ? this.$store.getters['users/find'](i) : null
      return operator ? `${operator.name.first} ${operator.name.last}` : 'N/A'
    },

    formatLabel(composite, data) {
      let response = null

      if (composite) {
        response = `
          <div class="label">
            <span class="${(data[0].small) ? 'small' : ''}">${data[0].value}</span><br>
            <span class="${(data[1].small) ? 'small' : ''}">${data[1].value}</span>
          </div>
        `
      } else {
        response = data
      }

      return response
    },

    onRowClick(params) {
      this.$router.push({ name: 'appointment-single', params: { id: params.row.id } })
    },

    sortAppointmentDate(sx, sy) {
      const x = sx.d.concat(sx.b)
      const y = sy.d.concat(sy.b)
      let response = null

      if (x < y) {
        response = -1
      } else if (x > y) {
        response = 1
      } else {
        response = 0
      }

      return response
    },

    rowStyleClassFn(row) {
      let rowClass = null

      if (this.isDentist) {
        rowClass = row.read ? '' : 'new'
      }

      return rowClass
    },

    calculateFiltersWidth() {
      const filters = document.querySelector('.block-filters')

      if (filters.offsetWidth < '752') {
        filters.classList.add('medium')
      } else {
        filters.classList.remove('medium')
      }

      if (filters.offsetWidth < '448') {
        filters.classList.add('small')
      } else {
        filters.classList.remove('small')
      }
    },

    clearFilters() {
      this.filters = {
        search: null,
        dentist: null,
        date: null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .block-filters {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-bottom: 1em;
    padding: 1em;

    .filters {
      display: flex;
      flex-wrap: wrap;

      .filter {
        min-width: 200px;
        margin-right: 1em;

        .filter-search {
          display: flex;

          .search-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 30px;
            height: 30px;
            background-color: $colour-primary;
            color: $colour-light;

            .icon {
              --ggs: .8;
            }
          }

          .search-input {
            width: 170px;
          }
        }

        .filter-dentist {
          width: 200px;
        }
      }
    }

    .actions {
      display: flex;
      flex-wrap: wrap;

      .action {
        flex: none;

        &:not(:last-of-type) {
          margin-right: .5em;
        }
      }
    }

    &.medium {
      justify-content: space-around;

      .filters .filter:last-of-type {
        margin-right: 0;
        margin-bottom: 1em;
      }
    }

    &.small {
      justify-content: space-around;

      .filters {
        justify-content: space-around;

        .filter {
          margin-right: 0;
          margin-bottom: 1em;
        }
      }
    }
  }
</style>
